import { Analytics24, Edit24, View24 } from 'ui/icons';
import { CampaignAlert } from '../../hooks';
import { CampaignState } from '../../../../domains/campaign';
import { CampaignAlertHistorySource } from './types';

export const editCampaignPath = (campaignId: number) => `/campaigns/${campaignId}?view=Edit`;
export const viewCampaignPath = (campaignId: number) => `/campaigns/${campaignId}?view=Summary`;
export const timeSeriesPath = (campaignId: number, selectedAlert: CampaignAlert | null) =>
  `/analytics/campaignPerformance/timeSeries?campaignId=${campaignId}&timeSeriesMetrics=${selectedAlert?.metricName}`;

export const editCampaignContent = (
  <>
    <Edit24 /> Edit campaign
  </>
);
export const viewCampaignContent = (
  <>
    <View24 /> View campaign
  </>
);
export const viewTimeSeriesContent = (
  <>
    <Analytics24 /> View time series
  </>
);

export type ContentType = {
  source: CampaignAlertHistorySource;
  state: CampaignState;
  label: JSX.Element | string | null;
  route: string;
  isShown: boolean;
};

const mappingByCampaignIndexPage = (
  campaignId: number,
  selectedAlert: CampaignAlert | null,
): ContentType[] => [
  {
    source: CampaignAlertHistorySource.CampaignIndex,
    state: CampaignState.Draft,
    label: selectedAlert ? viewTimeSeriesContent : editCampaignContent,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : editCampaignPath(campaignId),
    isShown: true,
  },
  {
    source: CampaignAlertHistorySource.CampaignIndex,
    state: CampaignState.Ready,
    label: selectedAlert ? viewTimeSeriesContent : editCampaignContent,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : editCampaignPath(campaignId),
    isShown: true,
  },
  {
    source: CampaignAlertHistorySource.CampaignIndex,
    state: CampaignState.Running,
    label: selectedAlert ? viewTimeSeriesContent : viewCampaignContent,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : viewCampaignPath(campaignId),
    isShown: true,
  },
  {
    source: CampaignAlertHistorySource.CampaignIndex,
    state: CampaignState.Aborted,
    label: selectedAlert ? viewTimeSeriesContent : viewCampaignContent,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : viewCampaignPath(campaignId),
    isShown: true,
  },
  {
    source: CampaignAlertHistorySource.CampaignIndex,
    state: CampaignState.Finished,
    label: selectedAlert ? viewTimeSeriesContent : viewCampaignContent,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : viewCampaignPath(campaignId),
    isShown: true,
  },
  {
    source: CampaignAlertHistorySource.CampaignIndex,
    state: CampaignState.Archived,
    label: selectedAlert ? viewTimeSeriesContent : viewCampaignContent,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : viewCampaignPath(campaignId),
    isShown: true,
  },
];

const mappingByCampaignEditorPage = (campaignId: number, selectedAlert: CampaignAlert | null) => [
  {
    source: CampaignAlertHistorySource.CampaignEditor,
    state: CampaignState.Draft,
    label: '',
    route: '',
    isShown: false,
  },
  {
    source: CampaignAlertHistorySource.CampaignEditor,
    state: CampaignState.Ready,
    label: selectedAlert ? viewTimeSeriesContent : editCampaignContent,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : editCampaignPath(campaignId),
    isShown: true,
  },
  {
    source: CampaignAlertHistorySource.CampaignEditor,
    state: CampaignState.Running,
    label: selectedAlert ? viewTimeSeriesContent : null,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : '',
    isShown: !!selectedAlert,
  },
  {
    source: CampaignAlertHistorySource.CampaignEditor,
    state: CampaignState.Aborted,
    label: selectedAlert ? viewTimeSeriesContent : null,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : '',
    isShown: !!selectedAlert,
  },
  {
    source: CampaignAlertHistorySource.CampaignEditor,
    state: CampaignState.Finished,
    label: selectedAlert ? viewTimeSeriesContent : null,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : '',
    isShown: !!selectedAlert,
  },
  {
    source: CampaignAlertHistorySource.CampaignEditor,
    state: CampaignState.Archived,
    label: selectedAlert ? viewTimeSeriesContent : null,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : '',
    isShown: !!selectedAlert,
  },
];

const mappingByCampaignAnalytics = (
  campaignId: number,
  selectedAlert: CampaignAlert | null,
  pathname: string,
) => [
  {
    source: CampaignAlertHistorySource.CampaignAnalytics,
    state: CampaignState.Draft,
    label: '',
    route: '',
    isShown: false,
  },
  {
    source: CampaignAlertHistorySource.CampaignAnalytics,
    state: CampaignState.Ready,
    label: '',
    route: '',
    isShown: false,
  },
  {
    source: CampaignAlertHistorySource.CampaignAnalytics,
    state: CampaignState.Running,
    label: selectedAlert ? viewTimeSeriesContent : viewCampaignContent,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : viewCampaignPath(campaignId),
    isShown: !selectedAlert || !pathname.includes('timeSeries'),
  },
  {
    source: CampaignAlertHistorySource.CampaignAnalytics,
    state: CampaignState.Aborted,
    label: selectedAlert ? viewTimeSeriesContent : viewCampaignContent,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : viewCampaignPath(campaignId),
    isShown: !selectedAlert || !pathname.includes('timeSeries'),
  },
  {
    source: CampaignAlertHistorySource.CampaignAnalytics,
    state: CampaignState.Finished,
    label: selectedAlert ? viewTimeSeriesContent : viewCampaignContent,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : viewCampaignPath(campaignId),
    isShown: !selectedAlert || !pathname.includes('timeSeries'),
  },
  {
    source: CampaignAlertHistorySource.CampaignAnalytics,
    state: CampaignState.Archived,
    label: selectedAlert ? viewTimeSeriesContent : viewCampaignContent,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : viewCampaignPath(campaignId),
    isShown: !selectedAlert || !pathname.includes('timeSeries'),
  },
];

const mappingByHomepage = (campaignId: number, selectedAlert: CampaignAlert | null) => [
  {
    source: CampaignAlertHistorySource.Homepage,
    state: CampaignState.Draft,
    label: viewCampaignContent,
    route: viewCampaignPath(campaignId),
    isShown: true,
  },
  {
    source: CampaignAlertHistorySource.Homepage,
    state: CampaignState.Ready,
    label: viewCampaignContent,
    route: viewCampaignPath(campaignId),
    isShown: true,
  },
  {
    source: CampaignAlertHistorySource.Homepage,
    state: CampaignState.Running,
    label: selectedAlert ? viewTimeSeriesContent : viewCampaignContent,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : viewCampaignPath(campaignId),
    isShown: true,
  },
  {
    source: CampaignAlertHistorySource.Homepage,
    state: CampaignState.Aborted,
    label: selectedAlert ? viewTimeSeriesContent : viewCampaignContent,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : viewCampaignPath(campaignId),
    isShown: true,
  },
  {
    source: CampaignAlertHistorySource.Homepage,
    state: CampaignState.Finished,
    label: selectedAlert ? viewTimeSeriesContent : viewCampaignContent,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : viewCampaignPath(campaignId),
    isShown: true,
  },
  {
    source: CampaignAlertHistorySource.Homepage,
    state: CampaignState.Archived,
    label: selectedAlert ? viewTimeSeriesContent : viewCampaignContent,
    route: selectedAlert ? timeSeriesPath(campaignId, selectedAlert) : viewCampaignPath(campaignId),
    isShown: true,
  },
];

type AlertHistoryButtonContentProps = {
  campaignId: number;
  selectedAlert: CampaignAlert | null;
  pathname: string;
  source: CampaignAlertHistorySource;
  campaignState: CampaignState;
};
export const mappingByPageAndSource = ({
  campaignId,
  selectedAlert,
  pathname,
  source,
  campaignState,
}: AlertHistoryButtonContentProps) => {
  const mappings = [
    ...mappingByCampaignIndexPage(campaignId, selectedAlert),
    ...mappingByCampaignEditorPage(campaignId, selectedAlert),
    ...mappingByCampaignAnalytics(campaignId, selectedAlert, pathname),
    ...mappingByHomepage(campaignId, selectedAlert),
  ].filter(
    (content) => content.isShown && source === content.source && campaignState === content.state,
  );
  return mappings[0];
};
